import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { hasHigherOrEqualRole } from '@/lib/permissions';
import {
  getIntegrationStatuses,
  getSystemHealth,
  updateIntegrationStatus,
  logIntegrationTest,
  logSystemHealthCheck,
  IntegrationStatusRecord,
  IntegrationStatus as IntStatus,
  SystemHealthStatus,
} from '@/lib/integrations-service';
import IntegrationLogsTable from '@/components/admin/IntegrationLogsTable';
import CalendlyConnect from '@/components/admin/CalendlyConnect';
import JotformConnect from '@/components/admin/JotformConnect';
import {
  Calendar,
  CreditCard,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Send,
  ShieldCheck,
  ShieldAlert,
  PlayCircle,
  Clock,
  Activity,
  Zap,
  Server,
  Bell,
  ArrowRight,
  Sparkles,
  Timer,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

type IntegrationStatus = 'connected' | 'not_configured' | 'error' | 'unknown';

interface IntegrationResult {
  success: boolean;
  status: IntegrationStatus;
  message: string;
  configured: Record<string, boolean>;
  mode?: string | null;
  provider?: string | null;
  accountId?: string;
  businessName?: string | null;
}

const StatusIndicator = ({ status, pulse = false }: { status: IntStatus | 'unknown'; pulse?: boolean }) => {
  const config: Record<string, { color: string; bgColor: string; label: string }> = {
    success: { color: 'bg-emerald-500', bgColor: 'bg-emerald-500/20', label: 'Connected' },
    connected: { color: 'bg-emerald-500', bgColor: 'bg-emerald-500/20', label: 'Connected' },
    warning: { color: 'bg-amber-500', bgColor: 'bg-amber-500/20', label: 'Warning' },
    error: { color: 'bg-red-500', bgColor: 'bg-red-500/20', label: 'Error' },
    not_configured: { color: 'bg-slate-400', bgColor: 'bg-slate-400/20', label: 'Not Configured' },
    not_checked: { color: 'bg-slate-400', bgColor: 'bg-slate-400/20', label: 'Not Checked' },
    unknown: { color: 'bg-slate-400', bgColor: 'bg-slate-400/20', label: 'Unknown' },
  };

  const { color, bgColor, label } = config[status] || config.unknown;

  return (
    <div className="flex items-center gap-2">
      <div className={`relative h-3 w-3 rounded-full ${bgColor}`}>
        <div className={`absolute inset-0.5 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`} />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
};

const HealthScoreRing = ({ score, label }: { score: number; label: string }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const color = score === 100 ? '#10b981' : score >= 66 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-muted/30"
        />
        <motion.circle
          cx="64"
          cy="64"
          r="45"
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold">{score}%</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
};

const AdminIntegrations = () => {
  const { toast } = useToast();
  const { role, user } = useAuth();
  const canTest = hasHigherOrEqualRole(role, 'admin');

  // System health
  const [health, setHealth] = useState<SystemHealthStatus | null>(null);
  const [storedStatuses, setStoredStatuses] = useState<IntegrationStatusRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Individual test states
  const [calendlyLoading, setCalendlyLoading] = useState(false);
  const [calendlyResult, setCalendlyResult] = useState<IntegrationResult | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeResult, setStripeResult] = useState<IntegrationResult | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState<IntegrationResult | null>(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Run All Tests state
  const [runningAllTests, setRunningAllTests] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  const [logsRefreshKey, setLogsRefreshKey] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthData, statuses] = await Promise.all([
        getSystemHealth(),
        getIntegrationStatuses(),
      ]);
      setHealth(healthData);
      setStoredStatuses(statuses);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStoredStatus = (name: string) => storedStatuses.find(s => s.integration_name === name);

  const calculateHealthScore = () => {
    if (!health) return 0;
    const integrations = [
      health.integrations.calendly,
      health.integrations.stripe,
      health.integrations.email,
    ];
    const successCount = integrations.filter(i => i?.last_status === 'success').length;
    return Math.round((successCount / 3) * 100);
  };

  const testCalendly = async (): Promise<IntegrationResult> => {
    setCalendlyLoading(true);
    setCalendlyResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('calendly-api', {
        body: { action: 'test-connection' }
      });
      if (error) throw error;
      
      const result: IntegrationResult = {
        success: data.success,
        status: data.success ? 'connected' : 'error',
        message: data.message || (data.success ? 'Calendly connected successfully' : 'Connection failed'),
        configured: { CALENDLY_API_KEY: data.success },
      };
      setCalendlyResult(result);

      const status: IntStatus = data.success ? 'success' : 'error';
      await updateIntegrationStatus('calendly', status, result.message);
      await logIntegrationTest('calendly', data.success ? 'success' : 'error', result.message, { user: data.user?.email }, user?.id);
      
      setLogsRefreshKey(k => k + 1);
      await fetchData();
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const result: IntegrationResult = { success: false, status: 'error', message, configured: {} };
      setCalendlyResult(result);
      try {
        await updateIntegrationStatus('calendly', 'error', message);
        await logIntegrationTest('calendly', 'error', message, { error: String(error) }, user?.id);
        setLogsRefreshKey(k => k + 1);
        await fetchData();
      } catch (e) { console.error('Error logging:', e); }
      return result;
    } finally {
      setCalendlyLoading(false);
    }
  };

  const testStripe = async (): Promise<IntegrationResult> => {
    setStripeLoading(true);
    setStripeResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('test-stripe');
      if (error) throw error;
      setStripeResult(data);

      const status: IntStatus = data.success ? 'success' : data.status === 'not_configured' ? 'not_configured' : 'error';
      await updateIntegrationStatus('stripe', status, data.message);
      await logIntegrationTest('stripe', data.success ? 'success' : 'error', data.message, { configured: data.configured, mode: data.mode }, user?.id);
      
      setLogsRefreshKey(k => k + 1);
      await fetchData();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const result: IntegrationResult = { success: false, status: 'error', message, configured: {} };
      setStripeResult(result);
      try {
        await updateIntegrationStatus('stripe', 'error', message);
        await logIntegrationTest('stripe', 'error', message, { error: String(error) }, user?.id);
        setLogsRefreshKey(k => k + 1);
        await fetchData();
      } catch (e) { console.error('Error logging:', e); }
      return result;
    } finally {
      setStripeLoading(false);
    }
  };

  const testEmailConnection = async (): Promise<IntegrationResult> => {
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('test-email', { body: {} });
      if (error) throw error;
      setEmailResult(data);

      const status: IntStatus = data.success ? 'success' : data.status === 'not_configured' ? 'not_configured' : 'error';
      await updateIntegrationStatus('email', status, data.message);
      await logIntegrationTest('email', data.success ? 'success' : 'error', data.message, { configured: data.configured, provider: data.provider }, user?.id);
      
      setLogsRefreshKey(k => k + 1);
      await fetchData();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const result: IntegrationResult = { success: false, status: 'error', message, configured: {} };
      setEmailResult(result);
      try {
        await updateIntegrationStatus('email', 'error', message);
        await logIntegrationTest('email', 'error', message, { error: String(error) }, user?.id);
        setLogsRefreshKey(k => k + 1);
        await fetchData();
      } catch (e) { console.error('Error logging:', e); }
      return result;
    } finally {
      setEmailLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    setSendingTestEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-email', {
        body: { sendTestEmail: true, testEmailAddress },
      });
      if (error) throw error;
      await logIntegrationTest('email', data.success ? 'success' : 'error', data.message, { testEmailAddress }, user?.id);
      setLogsRefreshKey(k => k + 1);
      toast({
        title: data.success ? 'Test Email Sent' : 'Email Test Failed',
        description: data.message,
        variant: data.success ? 'default' : 'destructive',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast({ title: 'Test Failed', description: message, variant: 'destructive' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const runAllTests = async () => {
    setRunningAllTests(true);
    setTestProgress(0);

    try {
      setTestProgress(10);
      const calendlyRes = await testCalendly();
      setTestProgress(40);
      const stripeRes = await testStripe();
      setTestProgress(70);
      const emailRes = await testEmailConnection();
      setTestProgress(100);

      const totalPassed = [calendlyRes, stripeRes, emailRes].filter(r => r.success).length;
      const overall = totalPassed === 3 ? 'healthy' : totalPassed === 0 ? 'down' : 'degraded';
      
      await logSystemHealthCheck(overall as any, `Health check: ${totalPassed}/3 passed`, {
        calendly: calendlyRes.success,
        stripe: stripeRes.success,
        email: emailRes.success,
      }, user?.id);
      setLogsRefreshKey(k => k + 1);

      toast({
        title: totalPassed === 3 ? 'All Tests Passed!' : `${totalPassed}/3 Tests Passed`,
        description: totalPassed === 3 ? 'All integrations are working correctly.' : `${3 - totalPassed} integration(s) need attention.`,
        variant: totalPassed === 3 ? 'default' : 'destructive',
      });
    } catch (error) {
      toast({ title: 'Test Suite Failed', description: 'An error occurred while running tests.', variant: 'destructive' });
    } finally {
      setRunningAllTests(false);
      setTestProgress(0);
    }
  };

  const isAnyTestRunning = calendlyLoading || stripeLoading || emailLoading || runningAllTests;
  const calendlyStatus = getStoredStatus('calendly');
  const stripeStatus = getStoredStatus('stripe');
  const emailStatus = getStoredStatus('email');

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <Skeleton className="h-10 w-48 sm:w-64" />
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 lg:col-span-3" />
        </div>
        <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Integrations - Admin" description="Manage and test third-party integrations" />
      
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Integrations</h1>
            <p className="text-sm text-muted-foreground mt-1">Monitor and manage third-party connections</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button variant="outline" onClick={fetchData} disabled={isAnyTestRunning} size="sm" className="sm:size-default">
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${isAnyTestRunning ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button onClick={runAllTests} disabled={isAnyTestRunning || !canTest} size="sm" className="gap-2 sm:size-default">
              {runningAllTests ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Testing...</span>
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  <span className="hidden sm:inline">Run All Tests</span>
                  <span className="sm:hidden">Test All</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Progress bar during tests */}
        <AnimatePresence>
          {runningAllTests && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Card className="border-primary/50 bg-primary/5 overflow-hidden">
                <CardContent className="pt-4 pb-4 sm:pt-6 sm:pb-6">
                  <div className="flex items-center gap-3 sm:gap-4 mb-2">
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-primary shrink-0" />
                    <span className="font-medium text-sm sm:text-base">Running tests...</span>
                    <span className="text-xs sm:text-sm text-muted-foreground ml-auto">{testProgress}%</span>
                  </div>
                  <Progress value={testProgress} className="h-2" />
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Health Overview */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-4">
          {/* Health Score */}
          <Card className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
            <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center min-h-[200px]">
              <HealthScoreRing score={calculateHealthScore()} label="Health Score" />
              <Badge 
                className={`mt-4 text-xs ${
                  health?.overall === 'healthy' 
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' 
                    : health?.overall === 'degraded'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {health?.overall === 'healthy' ? 'All Systems Operational' : health?.overall === 'degraded' ? 'Degraded Performance' : 'System Issues'}
              </Badge>
            </CardContent>
          </Card>

          {/* Quick Status Cards */}
          <Card className="lg:col-span-3 overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-5 w-5" />
                Integration Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
                {/* Calendly */}
                <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all overflow-hidden ${
                  calendlyStatus?.last_status === 'success' 
                    ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20' 
                    : calendlyStatus?.last_status === 'error'
                    ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                    : 'border-muted bg-muted/30'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg shrink-0 ${
                      calendlyStatus?.last_status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-muted'
                    }`}>
                      <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        calendlyStatus?.last_status === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">Calendly</p>
                      <p className="text-xs text-muted-foreground">Scheduling</p>
                    </div>
                  </div>
                  <StatusIndicator status={calendlyStatus?.last_status || 'not_checked'} pulse={calendlyLoading} />
                  {calendlyStatus?.last_checked_at && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1 truncate">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span className="truncate">{formatDistanceToNow(new Date(calendlyStatus.last_checked_at), { addSuffix: true })}</span>
                    </p>
                  )}
                </div>

                {/* Stripe */}
                <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all overflow-hidden ${
                  stripeStatus?.last_status === 'success' 
                    ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20' 
                    : stripeStatus?.last_status === 'error'
                    ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                    : 'border-muted bg-muted/30'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg shrink-0 ${
                      stripeStatus?.last_status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-muted'
                    }`}>
                      <CreditCard className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        stripeStatus?.last_status === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">Stripe</p>
                      <p className="text-xs text-muted-foreground">Payments</p>
                    </div>
                  </div>
                  <StatusIndicator status={stripeStatus?.last_status || 'not_checked'} pulse={stripeLoading} />
                  {stripeStatus?.last_checked_at && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1 truncate">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span className="truncate">{formatDistanceToNow(new Date(stripeStatus.last_checked_at), { addSuffix: true })}</span>
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all overflow-hidden ${
                  emailStatus?.last_status === 'success' 
                    ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20' 
                    : emailStatus?.last_status === 'error'
                    ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                    : 'border-muted bg-muted/30'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg shrink-0 ${
                      emailStatus?.last_status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-muted'
                    }`}>
                      <Mail className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        emailStatus?.last_status === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">Email Service</p>
                      <p className="text-xs text-muted-foreground">Notifications</p>
                    </div>
                  </div>
                  <StatusIndicator status={emailStatus?.last_status || 'not_checked'} pulse={emailLoading} />
                  {emailStatus?.last_checked_at && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1 truncate">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span className="truncate">{formatDistanceToNow(new Date(emailStatus.last_checked_at), { addSuffix: true })}</span>
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scheduled Health Check Info */}
        <Card className="border-dashed border-2 bg-muted/30 overflow-hidden">
          <CardContent className="pt-4 pb-4 sm:pt-6 sm:pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="p-2.5 sm:p-3 rounded-xl bg-primary/10 shrink-0">
                <Timer className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm sm:text-base">Automated Health Checks</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Health checks run daily at 6:00 AM UTC. Email alerts sent on failures.
                </p>
              </div>
              <Badge variant="outline" className="flex items-center gap-1 text-xs shrink-0">
                <Bell className="h-3 w-3" />
                Alerts Enabled
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for detailed view */}
        <Tabs defaultValue="details" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-3 sm:w-[320px] lg:w-[400px]">
            <TabsTrigger value="details" className="text-xs sm:text-sm">Details</TabsTrigger>
            <TabsTrigger value="testing" className="text-xs sm:text-sm">Testing</TabsTrigger>
            <TabsTrigger value="logs" className="text-xs sm:text-sm">Logs</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            {/* Connection Cards */}
            <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
              <CalendlyConnect />
              <JotformConnect />

              {/* Stripe Detail Card */}
              <Card className="overflow-hidden flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-violet-100 dark:bg-violet-900/30 shrink-0">
                      <CreditCard className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base">Stripe</CardTitle>
                      <CardDescription className="text-xs">Payment processing</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Configuration</p>
                    <div className="space-y-2 bg-muted/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                          stripeResult?.configured?.STRIPE_SECRET_KEY ? 'bg-emerald-500' : 'bg-slate-300'
                        }`} />
                        <code className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                          STRIPE_SECRET_KEY
                        </code>
                      </div>
                    </div>
                  </div>
                  {stripeResult?.mode && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Mode:</span>
                      <Badge variant={stripeResult.mode === 'live' ? 'default' : 'secondary'} className="flex items-center gap-1 text-xs">
                        {stripeResult.mode === 'live' ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
                        {stripeResult.mode === 'live' ? 'Live' : 'Test'}
                      </Badge>
                    </div>
                  )}
                  {stripeStatus?.last_message && (
                    <div className={`p-3 rounded-lg text-xs leading-relaxed mt-auto ${
                      stripeStatus.last_status === 'success'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                    }`}>
                      <p className="break-words [overflow-wrap:anywhere]">{stripeStatus.last_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Email Detail Card */}
              <Card className="overflow-hidden flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-rose-100 dark:bg-rose-900/30 shrink-0">
                      <Mail className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base">Email Service</CardTitle>
                      <CardDescription className="text-xs">Resend notifications</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Configuration</p>
                    <div className="space-y-2 bg-muted/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                          emailResult?.configured?.RESEND_API_KEY ? 'bg-emerald-500' : 'bg-slate-300'
                        }`} />
                        <code className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                          RESEND_API_KEY
                        </code>
                      </div>
                    </div>
                  </div>
                  {emailStatus?.last_message && (
                    <div className={`p-3 rounded-lg text-xs leading-relaxed mt-auto ${
                      emailStatus.last_status === 'success'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                    }`}>
                      <p className="break-words [overflow-wrap:anywhere]">{emailStatus.last_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Testing Tab */}
          <TabsContent value="testing" className="space-y-6">
            <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {/* Calendly Test */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-500 shrink-0" />
                    <span>Calendly</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    onClick={async () => {
                      const result = await testCalendly();
                      toast({
                        title: result.success ? 'Test Passed' : 'Test Failed',
                        description: result.message,
                        variant: result.success ? 'default' : 'destructive',
                      });
                    }}
                    disabled={calendlyLoading || !canTest}
                    className="w-full"
                  >
                    {calendlyLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</>
                    ) : (
                      <><PlayCircle className="h-4 w-4 mr-2" />Test Connection</>
                    )}
                  </Button>
                  {calendlyResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg text-xs leading-relaxed ${
                        calendlyResult.success
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                      }`}
                    >
                      <p className="break-words [overflow-wrap:anywhere]">{calendlyResult.message}</p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              {/* Stripe Test */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-violet-500 shrink-0" />
                    <span>Stripe</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    onClick={async () => {
                      const result = await testStripe();
                      toast({
                        title: result.success ? 'Test Passed' : 'Test Failed',
                        description: result.message,
                        variant: result.success ? 'default' : 'destructive',
                      });
                    }}
                    disabled={stripeLoading || !canTest}
                    className="w-full"
                  >
                    {stripeLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</>
                    ) : (
                      <><PlayCircle className="h-4 w-4 mr-2" />Test Connection</>
                    )}
                  </Button>
                  {stripeResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg text-xs leading-relaxed ${
                        stripeResult.success
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                      }`}
                    >
                      <p className="break-words [overflow-wrap:anywhere]">{stripeResult.message}</p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              {/* Email Test */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="h-5 w-5 text-rose-500 shrink-0" />
                    <span>Email Service</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    onClick={async () => {
                      const result = await testEmailConnection();
                      toast({
                        title: result.success ? 'Test Passed' : 'Test Failed',
                        description: result.message,
                        variant: result.success ? 'default' : 'destructive',
                      });
                    }}
                    disabled={emailLoading || !canTest}
                    className="w-full"
                  >
                    {emailLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</>
                    ) : (
                      <><PlayCircle className="h-4 w-4 mr-2" />Test Connection</>
                    )}
                  </Button>
                  
                  <div className="pt-3 border-t space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Send Test Email</p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="test@example.com"
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        disabled={!canTest}
                        className="text-sm"
                      />
                      <Button
                        onClick={sendTestEmail}
                        disabled={sendingTestEmail || !canTest || !testEmailAddress}
                        size="icon"
                        variant="secondary"
                        className="shrink-0"
                      >
                        {sendingTestEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {emailResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg text-xs leading-relaxed ${
                        emailResult.success
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                      }`}
                    >
                      <p className="break-words [overflow-wrap:anywhere]">{emailResult.message}</p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </div>

            {!canTest && (
              <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      You need Admin or Super Admin permissions to run integration tests.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <IntegrationLogsTable key={logsRefreshKey} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default AdminIntegrations;
